import { LockIcon, PlusIcon } from "lucide-react"
import {
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
  type ComponentProps,
  type FormEvent,
  type ReactNode,
} from "react"
import { useLocation, useNavigate } from "react-router"
import { Button } from "@/primitives/button.tsx"
import { Input } from "@/primitives/input.tsx"
import { Skeleton } from "@/primitives/skeleton.tsx"
import { Tooltip, TooltipContent, TooltipTrigger } from "@/primitives/tooltip.tsx"
import { cn } from "@/primitives/utils"
import { useShortcut } from "@/shortcuts/use-shortcut.ts"
import { useLadder, useRegistered } from "./page-chrome.tsx"
import { useShownAfter } from "./page-skeleton.tsx"
import { scrollBehavior } from "./scroll-behavior.ts"
import { currentAnchor } from "./settings.ts"
import { useViewport } from "./viewport.tsx"

/*
 * The parts of a settings page: titled sections, each holding a panel of rows, and an AnchorNav
 * beside them that follows the Member down the page. Project settings and the Agent page are built
 * from these, so every setting in the app sits in the same frame: a label column on the left, the
 * control on the right, a hairline between rows. On `compact` a row stacks its label over the
 * control, which takes the row's width, and a section's action drops under its description.
 */

/**
 * One titled section of a settings page, `id` being its anchor. `description` says what the
 * section is for, under the title; `action` is the section's own control, at the end of its header,
 * such as "Give access", or under the description on `compact`. `locked` marks a section the Member
 * can read but not change.
 */
export function SettingsSection({
  id,
  title,
  description,
  action,
  locked = false,
  children,
}: {
  id: string
  title: string
  description?: string
  action?: ReactNode
  locked?: boolean
  children: ReactNode
}) {
  // Arrived at by its anchor, it comes to rest 16px under the page's stuck chrome.
  const { sectionMargin } = useLadder()
  return (
    <section id={id} aria-labelledby={`${id}-title`} className={sectionMargin}>
      <div className="mb-3 flex items-end gap-4 compact:flex-col compact:items-start compact:gap-3">
        <div className="min-w-0 space-y-0.5">
          <h2 id={`${id}-title`} className="flex items-center gap-1.5 text-title font-semibold">
            {title}
            {locked && (
              <Tooltip>
                <TooltipTrigger render={<span className="inline-flex" />}>
                  <LockIcon
                    role="img"
                    aria-label="Read only"
                    className="size-3.5 text-subtle-foreground"
                  />
                </TooltipTrigger>
                <TooltipContent>Read only</TooltipContent>
              </Tooltip>
            )}
          </h2>
          {description && (
            <p className="max-w-prose text-ui text-muted-foreground">{description}</p>
          )}
        </div>
        {action && (
          <div className="ml-auto flex shrink-0 items-center gap-2 compact:ml-0">{action}</div>
        )}
      </div>
      {children}
    </section>
  )
}

const panelTones = {
  default: "ring-border",
  danger: "ring-destructive/25",
} as const

/**
 * The card a section's rows rest on, a hairline between each. `danger` rims it in red, for the
 * section whose actions can't be taken back. Its first and last rows take its rounded corners, so a
 * row's hover fill stays inside them.
 */
export function SettingsPanel({
  tone = "default",
  children,
}: {
  tone?: keyof typeof panelTones
  children: ReactNode
}) {
  return (
    <div
      data-slot="settings-panel"
      className={cn(
        "divide-y divide-border-subtle rounded-lg bg-card shadow-raised ring-1 *:first:rounded-t-lg *:last:rounded-b-lg",
        panelTones[tone],
      )}
    >
      {children}
    </div>
  )
}

/*
 * A list row's height: `default` for a line of text and controls, `tall` for one led by a 24px
 * avatar or tile, `auto` for one whose content wraps onto more lines, such as a Slack rule and its
 * events. On `compact` a `tall` row grows with a name that wraps to a second line.
 */
const listHeights = {
  default: "h-10",
  tall: "h-11 compact:h-auto compact:min-h-11 compact:py-1.5",
  auto: "min-h-10 py-2.5",
} as const

/*
 * Where a list row's `secondary` line starts on `compact`: under the name, past what leads the row,
 * a 16px glyph or a 24px avatar or tile, 12px from the name, or a `MemberIdentity`'s 24px avatar,
 * 8px from it.
 */
const secondaryIndents = { glyph: "pl-7", tile: "pl-9", identity: "pl-8" } as const

/*
 * How an inline row shares its width. A `field` (a select, a switch, an input) sits right of a
 * label column of fixed width, so the controls of a panel's rows line up. A `button`, the row's one
 * action, takes only its own width at the end and leaves the rest to the label and its helper,
 * which would otherwise wrap into a narrow column beside a wide gap. A `segmented` choice (a tool's
 * Always allow, Needs approval or Deny) takes its own width as a button does on the desk, so what
 * the row names reads on one line beside it. A `switch` sits as a field does on the desk. On
 * `compact` a `field`, `button` or `segmented` row stacks its label and helper over the control: a
 * `field` or `segmented` takes the row's width, a `button` its own. A `switch` stays at the end of
 * its label's line, the helper running under the label beside it, as a phone's settings have it.
 */
const inlineRows = {
  field: "compact:flex-col compact:items-stretch compact:gap-2",
  button: "compact:flex-col compact:items-start compact:gap-3",
  segmented: "compact:flex-col compact:items-stretch compact:gap-2",
  switch: "compact:items-start compact:gap-4",
} as const

const inlineHeadings = {
  field: "w-60 shrink-0 compact:w-auto",
  button: "min-w-0 flex-1 compact:flex-none",
  segmented: "min-w-0 flex-1 compact:flex-none",
  switch: "w-60 shrink-0 compact:w-auto compact:min-w-0 compact:flex-1",
} as const

// A switch's box is its label's line, so the switch centres on that line however the helper runs.
const inlineControls = {
  field: "flex min-w-0 flex-1 justify-end compact:justify-start",
  button: "ml-auto flex shrink-0 compact:ml-0",
  segmented:
    "ml-auto flex shrink-0 compact:ml-0 compact:min-w-0 compact:flex-1 compact:justify-start",
  switch:
    "flex min-w-0 flex-1 justify-end compact:h-lh compact:flex-none compact:items-center compact:text-ui",
} as const

/*
 * How wide a field is on the desk, so the fields of a panel's rows line up whatever control each
 * is: `sm` a short default (a new Status's name), `md` a name, an address or
 * a picker, `lg` a column of controls (the Agent's model, its price and its Effort), `fill` all
 * the row leaves it. On `compact` every one takes the full width under its label.
 */
const fieldWidths = { sm: "w-56", md: "w-72", lg: "w-96", fill: "w-full" } as const

/** An inline row's control, sized by its row: the same widths, set on the row's one child. */
const rowFieldWidths = {
  sm: "*:w-56",
  md: "*:w-72",
  lg: "*:w-96",
  fill: "*:w-full",
} as const satisfies Record<FieldWidth, string>

export type FieldWidth = keyof typeof fieldWidths

/**
 * The width of a field that stands outside a row's control column, as a form opened under a row
 * holds (Signing in's username and password): a row's on the desk, the full width on `compact`.
 */
export const fieldWidthOf = (width: FieldWidth) => cn(fieldWidths[width], "compact:w-full")

/**
 * One row of a settings panel, in one of three layouts. `inline` puts the `label` and its `helper`
 * in a column on the left and the control on the right; `stacked` puts the label above a control
 * that needs the width, such as a description; `list` is a row of a list (a grant, a Status, a
 * Slack rule) whose children compose it, its actions revealed on hover (`group/row`). `htmlFor`
 * ties the label to the control it names. `control` is what an inline row holds: a `field` (the
 * default), a single `button`, as the danger zone's rows do, a `segmented` choice, which fills the
 * row on `compact` as a field does, or a `switch` (Move status on merge), which keeps its label's
 * line on `compact`. `fieldWidth` sizes a field row's
 * one control, so callers never set a width of their own; without it the control keeps its own
 * width (a Project's key, a person's Groups).
 *
 * A list row's `secondary` is what a phone has no room for beside the row's name: on `compact` it
 * is a second line under the name, in `text-xs`, and the desk, which shows the same fact inline,
 * leaves it out (the caller marks its inline copy `compact:hidden`). `lead` says what stands before
 * the name, so the line starts under it. On `compact` a list row's ⋯ shows without a hover, as a
 * stacked table row's does.
 */
export function SettingsRow({
  label,
  helper,
  htmlFor,
  layout,
  height = "default",
  control = "field",
  fieldWidth,
  secondary,
  lead = "glyph",
  children,
}: {
  label?: string
  helper?: ReactNode
  htmlFor?: string
  layout: "inline" | "stacked" | "list"
  /** A `list` row's height. */
  height?: keyof typeof listHeights
  /** What an `inline` row's control is, which decides how the row shares its width. */
  control?: keyof typeof inlineHeadings
  /** How wide a `field` row's control is on the desk; on `compact` it fills the row. */
  fieldWidth?: FieldWidth
  /** A `list` row's second line on `compact`. */
  secondary?: ReactNode
  /** What leads a `list` row's name, which its `secondary` line starts past. */
  lead?: keyof typeof secondaryIndents
  children: ReactNode
}) {
  const { compact } = useViewport()
  if (layout === "list") {
    const row = "group/row px-4 transition-colors duration-100 ease-snappy hover:bg-accent"
    const revealed = compact || undefined
    if (secondary === undefined)
      return (
        <div
          data-revealed={revealed}
          className={cn(row, "flex items-center gap-3", listHeights[height])}
        >
          {children}
        </div>
      )
    // Its line keeps the row's height on the desk; on `compact` the second line goes under it,
    // lined up with the name.
    return (
      <div data-revealed={revealed} className={cn(row, "compact:py-2")}>
        <div
          className={cn(
            "flex items-center gap-3",
            listHeights[height],
            "compact:h-auto compact:min-h-0 compact:py-0",
          )}
        >
          {children}
        </div>
        <p
          className={cn(
            "-mt-1 hidden truncate text-xs text-subtle-foreground compact:block",
            secondaryIndents[lead],
          )}
        >
          {secondary}
        </p>
      </div>
    )
  }

  const heading = (label || helper) && (
    <div className={cn("space-y-0.5", layout === "inline" && inlineHeadings[control])}>
      {label &&
        (htmlFor ? (
          <label htmlFor={htmlFor} className="block text-ui font-medium">
            {label}
          </label>
        ) : (
          <p className="text-ui font-medium">{label}</p>
        ))}
      {helper && <div className="text-xs text-muted-foreground">{helper}</div>}
    </div>
  )

  if (layout === "stacked")
    return (
      <div className="space-y-2 px-4 py-3">
        {heading}
        {children}
      </div>
    )

  return (
    <div className={cn("flex min-h-12 items-center gap-6 px-4 py-3", inlineRows[control])}>
      {heading}
      <div
        className={cn(
          inlineControls[control],
          fieldWidth && [rowFieldWidths[fieldWidth], "compact:*:w-full"],
        )}
      >
        {children}
      </div>
    </div>
  )
}

/**
 * The last row of a list that grows, such as "Add Status". It expands in place: the caller renders
 * an AddRowForm where it stood. On `compact` a label too long for its line wraps, the row growing.
 */
export function AddRow({ label, onClick }: { label: string; onClick: () => void }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className="flex h-9 w-full items-center gap-2 px-4 text-ui text-muted-foreground compact:h-auto compact:min-h-11 compact:py-2.5 compact:text-left transition-colors duration-100 ease-snappy outline-none hover:bg-accent hover:text-foreground focus-visible:bg-accent focus-visible:text-foreground focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-inset"
    >
      <PlusIcon aria-hidden className="size-4 compact:shrink-0" />
      {label}
    </button>
  )
}

/**
 * What an AddRow opens into, in its place at the foot of its list: the new row's name (`field`,
 * the Input's own props), whatever else a new row needs (`children`, such as a Status's category),
 * then Cancel and the `submit` button, enabled while `ready`. Enter or ⌘↵ submits, and neither does
 * while it isn't `ready`; Esc, from anywhere in the form, cancels. `shortcut={false}` leaves ⌘↵
 * unbound, for a form that never bound it on the desk. On `compact` it wraps: the name takes the
 * width left beside `children`, and the buttons a line of their own at the end.
 */
export function AddRowForm({
  label,
  field,
  fieldWidth = "sm",
  submit,
  ready,
  shortcut = true,
  onSubmit,
  onCancel,
  children,
}: {
  /** The form's accessible name, such as "Add a Status". */
  label: string
  field: Omit<ComponentProps<typeof Input>, "className">
  /** How wide the name is on the desk. */
  fieldWidth?: Extract<FieldWidth, "sm" | "md">
  submit: string
  ready: boolean
  /** Whether ⌘↵ submits too. */
  shortcut?: boolean
  onSubmit: () => void
  onCancel: () => void
  children?: ReactNode
}) {
  const form = useRef<HTMLFormElement>(null)
  useShortcut("submit", () => form.current?.requestSubmit(), { within: form, enabled: shortcut })

  return (
    <form
      ref={form}
      aria-label={label}
      onSubmit={(event: FormEvent) => {
        event.preventDefault()
        // `requestSubmit()` submits past a disabled submit button, so ⌘↵ is held back here.
        if (ready) onSubmit()
      }}
      onKeyDown={(event) => {
        // A Select's list is portalled out of the form, and its own Esc closes only the list.
        const inForm = event.target instanceof Node && event.currentTarget.contains(event.target)
        if (event.key === "Escape" && inForm && !event.defaultPrevented) {
          event.preventDefault()
          onCancel()
        }
      }}
      className="flex h-11 items-center gap-2 px-4 compact:h-auto compact:flex-wrap compact:py-3"
    >
      <Input
        {...field}
        className={cn("h-7", fieldWidths[fieldWidth], "compact:w-auto compact:flex-1")}
      />
      {children}
      <div className="ml-auto flex items-center gap-1.5 compact:basis-full compact:justify-end">
        <Button type="button" variant="ghost" size="sm" onClick={onCancel}>
          Cancel
        </Button>
        <Button type="submit" size="sm" disabled={!ready}>
          {submit}
        </Button>
      </div>
    </form>
  )
}

/** The page's one scroll container, which the shell marks, or the viewport when there is none. */
function scrollRootOf(element: Element | null) {
  return element?.closest<HTMLElement>("[data-slot=page-scroll]") ?? null
}

function isAtBottom(root: HTMLElement | null) {
  if (root) return root.scrollTop + root.clientHeight >= root.scrollHeight - 2
  return window.scrollY + window.innerHeight >= document.documentElement.scrollHeight - 2
}

// How long, at most, arriving at a section keeps it in place while the sections above it load.
const settlingFor = 5000

// The keys that scroll the page, or move the focus along it, which the Member presses themselves.
const scrollKeys = new Set([
  "ArrowUp",
  "ArrowDown",
  "PageUp",
  "PageDown",
  "Home",
  "End",
  " ",
  "Tab",
])

/**
 * A settings page's table of contents, in its `nav` pane. The section being read is marked as the
 * Member scrolls; a click scrolls smoothly to its section and puts it in the address (#statuses), so
 * the address can be shared. Arriving with one, the page opens at that section.
 *
 * A section chosen by a click, or by arriving at it, stays marked until the Member scrolls on their
 * own, even when it is too short to reach the top of the page and the page stops below it.
 *
 * A section is read in a band from just under the page's stuck chrome to 45% of the way down
 * (`readingBand` on the sticky ladder), and lands 16px under that chrome (`sectionMargin`, which
 * SettingsSection takes). On `compact` it is a row of chips stuck under the header, which scrolls
 * sideways to keep the current one in view, and registers as chrome, so both follow it.
 */
export function AnchorNav({ items }: { items: readonly { id: string; label: string }[] }) {
  const { compact } = useViewport()
  useRegistered("anchors", compact)
  const { readingBand } = useLadder()
  const nav = useRef<HTMLElement>(null)
  const location = useLocation()
  const navigate = useNavigate()
  const ids = items.map(({ id }) => id)
  const key = ids.join(" ")
  const hashed = location.hash.slice(1)
  const [current, setCurrent] = useState<string | undefined>(
    ids.includes(hashed) ? hashed : undefined,
  )
  // The section the Member chose, by a click or the address. While its scroll runs, the sections
  // it passes are not the ones being read, and once it ends, the chosen one is.
  const clicked = useRef<{ id: string } | undefined>(undefined)

  useEffect(() => {
    const ids = key ? key.split(" ") : []
    const root = scrollRootOf(nav.current)
    const showing = new Set<string>()
    const follow = () => {
      const click = clicked.current
      if (click) return setCurrent(click.id)
      setCurrent((previous) => currentAnchor(ids, showing, isAtBottom(root), previous))
    }
    const observer = new IntersectionObserver(
      (entries) => {
        for (const entry of entries)
          if (entry.isIntersecting) showing.add(entry.target.id)
          else showing.delete(entry.target.id)
        follow()
      },
      { root, rootMargin: readingBand },
    )
    for (const id of ids) {
      const section = document.getElementById(id)
      if (section) observer.observe(section)
    }
    const scroller: HTMLElement | Window = root ?? window
    // The Member scrolling on their own, by wheel, touch, key or the scrollbar, lets go of the
    // section they chose. A press anywhere else, such as on a button in that section, doesn't.
    const release = () => {
      if (!clicked.current) return
      clicked.current = undefined
      follow()
    }
    const releaseByKey = (event: KeyboardEvent) => {
      if (scrollKeys.has(event.key)) release()
    }
    const releaseByScrollbar = (event: PointerEvent) => {
      if (event.target === (root ?? document.documentElement)) release()
    }
    scroller.addEventListener("scroll", follow, { passive: true })
    scroller.addEventListener("wheel", release, { passive: true })
    scroller.addEventListener("touchmove", release, { passive: true })
    window.addEventListener("pointerdown", releaseByScrollbar)
    window.addEventListener("keydown", releaseByKey)
    return () => {
      observer.disconnect()
      scroller.removeEventListener("scroll", follow)
      scroller.removeEventListener("wheel", release)
      scroller.removeEventListener("touchmove", release)
      window.removeEventListener("pointerdown", releaseByScrollbar)
      window.removeEventListener("keydown", releaseByKey)
    }
  }, [key, readingBand])

  // On `compact`, the current chip is scrolled into the row's view as it changes.
  useLayoutEffect(() => {
    const row = nav.current
    const chip = row?.querySelector<HTMLElement>("[aria-current]")
    if (!compact || !row || !chip) return
    if (
      chip.offsetLeft < row.scrollLeft ||
      chip.offsetLeft + chip.offsetWidth > row.scrollLeft + row.clientWidth
    )
      row.scrollTo({ left: chip.offsetLeft - 16, behavior: scrollBehavior() })
  }, [compact, current])

  // Arriving at #statuses opens the page there, once its section is on the page, and keeps it there
  // while the sections above it load and grow, until the Member scrolls on their own.
  // The address the page was opened at; a click's own #anchor later is not an arrival.
  const arrivedAt = useRef(hashed)
  const arrived = useRef(false)
  useEffect(() => {
    const id = arrivedAt.current
    if (arrived.current || !key.split(" ").includes(id)) return
    const section = document.getElementById(id)
    if (!section) return
    arrived.current = true
    clicked.current = { id }
    let settling = true
    const place = () => {
      if (clicked.current?.id === id) section.scrollIntoView({ block: "start" })
    }
    place()
    const growing = new ResizeObserver(place)
    growing.observe(section.parentElement ?? section)
    const settled = setTimeout(() => {
      settling = false
      growing.disconnect()
    }, settlingFor)
    return () => {
      growing.disconnect()
      clearTimeout(settled)
      // Undone before it settled (as a development build does, running each effect twice), the
      // arrival starts over when the effect runs again.
      if (settling && clicked.current?.id === id) arrived.current = false
    }
  }, [key])

  function go(id: string) {
    const section = document.getElementById(id)
    if (!section) return
    clicked.current = { id }
    setCurrent(id)
    section.scrollIntoView({ behavior: scrollBehavior(), block: "start" })
    void navigate(
      { pathname: location.pathname, search: location.search, hash: `#${id}` },
      { replace: true, preventScrollReset: true, state: location.state },
    )
  }

  return (
    <nav
      ref={nav}
      aria-label="On this page"
      className={
        compact
          ? "flex h-12 items-center gap-1.5 overflow-x-auto overscroll-x-contain border-b border-border-subtle bg-background px-4 no-scrollbar"
          : "flex flex-col gap-0.5"
      }
    >
      {items.map(({ id, label }) => (
        <a
          key={id}
          href={`#${id}`}
          aria-current={id === current ? "location" : undefined}
          onClick={(event) => {
            if (event.metaKey || event.ctrlKey || event.shiftKey || event.button !== 0) return
            event.preventDefault()
            go(id)
          }}
          className={cn(
            compact
              ? "flex h-8 shrink-0 touch-target items-center rounded-full px-3 text-ui whitespace-nowrap transition-colors duration-100 ease-snappy outline-none focus-visible:ring-2 focus-visible:ring-ring"
              : "flex h-7 items-center rounded-md px-2 text-ui transition-colors duration-100 ease-snappy outline-none focus-visible:ring-2 focus-visible:ring-ring",
            id === current
              ? "bg-selected font-medium text-foreground"
              : compact
                ? "text-muted-foreground ring-1 ring-border-subtle ring-inset hover:bg-accent hover:text-foreground"
                : "text-muted-foreground hover:bg-accent hover:text-foreground",
          )}
        >
          {label}
        </a>
      ))}
    </nav>
  )
}

/** Label widths, fixed so the rows read as varied names yet never move between renders. */
const widths = ["w-40", "w-28", "w-36", "w-24"] as const

/**
 * A section's panel while its list loads, on any settings page: skeleton rows at the height of the rows that will
 * replace them, `tall` for rows led by an avatar. Like a PageSkeleton it shows only once the load
 * has run a moment, but it holds its place meanwhile, so the sections under it never move.
 */
export function SectionSkeleton({ rows, height }: { rows: number; height: "default" | "tall" }) {
  const shown = useShownAfter()
  return (
    <div role="status" aria-busy aria-label="Loading">
      <div className={shown ? "animate-in duration-150 fade-in" : "invisible"}>
        <SettingsPanel>
          {widths.slice(0, rows).map((width) => (
            <SettingsRow key={width} layout="list" height={height}>
              <Skeleton className={cn("rounded-full", height === "tall" ? "size-6" : "size-4")} />
              <Skeleton className={cn("h-3", width)} />
              <Skeleton className="ml-auto h-3 w-16" />
            </SettingsRow>
          ))}
        </SettingsPanel>
      </div>
    </div>
  )
}
